import { html, render } from 'lit-html'
import { ifDefined } from 'lit-html/directives/if-defined'
import { register } from '@polleverywhere/hmr-custom-element'
import '@polleverywhere/icon-element'
import '@polleverywhere/control-set'
import './style.scss'

function iconTemplate (type, name) {
  return type && name ? html`
    <icon-element
      type='${ifDefined(type)}'
      name='${ifDefined(name)}'
    ></icon-element>
  ` : ''
}

function controlTemplate (primary, secondary) {
  return primary || secondary ? html`<control-set
    primary='${ifDefined(primary)}' secondary='${ifDefined(secondary)}' />` : ''
}

function subtitleTemplate (title) {
  return title ? html`<div>${title}</div>` : ''
}

function template (title, subtitle, controls = {}, icon = {}, level) {
  return html`
    <header>
      ${iconTemplate(icon.type, icon.name)}
      <section name='content'>
        <div class='element-snail-trail'>
          <div class='element-snail-trail__container'>
            <div class='element-snail-trail__content'>
              <div role='heading' aria-level='${ifDefined(level)}'>
                ${title}
              </div>
            </div>
          </div>
        </div>
        ${subtitleTemplate(subtitle)}
      </section>
      ${controlTemplate(controls.primary, controls.secondary)}
    </header>
  `
}

function renderTemplate () {
  if (!this.isConnected) return
  const title = this.getAttribute('title')
  const subtitle = this.getAttribute('subtitle')
  const controls = {
    primary: this.getAttribute('primary-controls') || undefined,
    secondary: this.getAttribute('secondary-controls') || undefined
  }
  const icon = {
    type: this.getAttribute('icon-type') || undefined,
    name: this.getAttribute('icon-name') || undefined
  }
  const level = this.getAttribute('level') || undefined

  render(template(title, subtitle, controls, icon, level), this)
}

/**
 * Section header renders a title section with optional subtitle, icon, and
 * button control set all on a single line. Consumers can also optionally
 * specify which heading level to use. For example, a top-level page header
 * would declare this by specifying a `level=1` attribute.
 *
 * @element section-header
 *
 * @fires click:[control-name] - Bubbles up click events from the internal
 *    `<control-set>` custom element.
 *
 * @attr {string} title - Renders first two initials as the profile tab icon.
 * @attr {string} subtitle - Sets the specified tab name as active.
 * @attr {string} primary-controls - Comma-separated list of control names to
 *    render as primary controls, ordered from most to least important.
 * @attr {string} secondary-controls - Comma-separated list of control names to
 *    render as secondary controls, ordered from most to least important.
 * @attr {string} icon-type - Pass along to <icon-element> to render icon.
 * @attr {string} icon-name - Pass along to <icon-element> to render icon.
 * @attr {string} level - Set aria heading level for this header.
 */
export default class SectionHeader extends HTMLElement {
  static get tagName () { return 'section-header' }
  static get observedAttributes () {
    return [
      'title',
      'subtitle',
      'primary-controls',
      'secondary-controls',
      'icon-type',
      'icon-name',
      'level'
    ]
  }

  connectedCallback () {
    renderTemplate.call(this)
  }

  attributeChangedCallback () {
    renderTemplate.call(this)
  }
}

register(module, SectionHeader.tagName, SectionHeader)
